import {
  type OnlineTransactionStatus,
  type OnlineTransactionStatusCode,
  Query,
} from "@gen/app-query";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { LoadingScreen } from "~/components/loading_screen";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import {
  SectionSideNav,
  type SectionSideNavItem,
} from "~/components/section_side_nav";
import { StatusChip } from "~/components/status_chip";
import { TransactionMoney } from "~/entities/transaction";

import { BuyerLedger } from "./BuyerLedger";
import { ExtrasSection } from "./ExtrasSection";
import { PaymentReviewBanner } from "./PaymentReviewBanner";
import { SellerLedger } from "./SellerLedger";
import { TransactionTimelineSection } from "./TransactionTimeline";
import { UserDetails } from "./UserDetails";

const isOnlineStatusCode = (
  status: string | undefined,
  statuses?: OnlineTransactionStatus[],
): status is OnlineTransactionStatusCode =>
  Boolean(status && statuses?.some((s) => s.code === status));

export function OnlineTransaction() {
  const { clientId, transactionId } = useParams({
    from: "/clients/$clientId/online/transactions/$transactionId",
    select: (params) => ({
      clientId: params.clientId,
      transactionId: Number(params.transactionId),
    }),
    shouldThrow: true,
  });

  const {
    data: onlineTransactionData,
    isFetching: isFetchingOnlineTransaction,
  } = Query.useCore_getClientOnlineTransactionQuery(clientId, transactionId, {
    enabled: !!clientId && transactionId !== undefined,
  });

  const { data: clientData } = Query.useCore_getClientQuery(clientId);

  const { data: statusData } = Query.useCore_getTransactionStatusesQuery();

  const statusMap = new Map<string, OnlineTransactionStatus>(
    (statusData?.online ?? []).map((s: OnlineTransactionStatus) => [s.code, s]),
  );

  const transactionMoney = onlineTransactionData
    ? new TransactionMoney(onlineTransactionData)
    : null;

  const isPaymentInReview =
    !!onlineTransactionData?.review_flagged &&
    !onlineTransactionData?.payment_review?.finished;

  const sideNavItems = useMemo<SectionSideNavItem[]>(() => {
    const items = [{ id: "summary", label: "Transaction Details" }];
    if (isPaymentInReview) {
      items.push({ id: "payment-review", label: "Under Review" });
    }
    items.push(
      { id: "buyer", label: "Buyer Overview" },
      { id: "seller", label: "Seller Overview" },
      { id: "timeline", label: "Timeline" },
      { id: "client", label: "Client" },
      { id: "extras", label: "Extras" },
    );
    return items;
  }, [isPaymentInReview]);

  const theme = useTheme();

  if (isFetchingOnlineTransaction) {
    return <LoadingScreen />;
  }

  if (!onlineTransactionData || !transactionId) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">Transaction not found.</Typography>
      </Stack>
    );
  }

  const statusKey = isOnlineStatusCode(
    onlineTransactionData.status,
    statusData?.online,
  )
    ? onlineTransactionData.status
    : undefined;
  const statusInfo = statusKey ? statusMap.get(statusKey) : undefined;

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: { lg: 4.5 },
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav items={sideNavItems} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          id="summary"
          flexDirection={{ xs: "column", lg: "row" }}
          spacing={{ xs: 2, lg: 5 }}
          sx={{
            mb: { xs: 3, lg: 5 },
            p: { xs: 2, sm: 3, lg: 6 },
            backgroundColor: "primary._states.hover",
            borderRadius: 2,
            scrollMarginTop: theme.spacing(10),
          }}
          useFlexGap
        >
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Client</Typography>
            <Typography variant="body2" data-testid="transaction-client-text">
              {(clientData?.name ?? onlineTransactionData.client_id) || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">
              Transaction total
            </Typography>
            <Typography variant="body2" data-testid="transaction-total-text">
              {transactionMoney?.getTotal().format() ?? "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Description</Typography>
            <Typography
              variant="body2"
              data-testid="transaction-description-text"
            >
              {onlineTransactionData.description || "N/A"}
            </Typography>
          </Stack>
          <Stack flexDirection="column" flexGrow={1}>
            <Typography variant="body1DashedDivider">Status</Typography>
            {statusInfo ? (
              <StatusChip
                label={statusInfo.label}
                color={statusInfo.color}
                dataTestId="transaction-status-text"
              />
            ) : (
              <Chip
                label={onlineTransactionData.status}
                data-testid="transaction-status-text"
              />
            )}
          </Stack>
        </Stack>

        <Grid size={{ xs: 12, lg: 1014 }}>
          {isPaymentInReview && (
            <Box
              id="payment-review"
              sx={{ scrollMarginTop: theme.spacing(10) }}
            >
              <PaymentReviewBanner isPaymentInReview={isPaymentInReview} />
            </Box>
          )}
          <Grid
            id="buyer"
            container
            spacing={{ xs: 3, lg: 6 }}
            sx={{
              mb: { xs: 3, lg: 5 },
              backgroundColor: "primary._states.hover",
              borderRadius: 2,
              height: "max-content",
              p: { xs: 2, lg: 0 },
              scrollMarginTop: theme.spacing(10),
            }}
          >
            <Grid
              sx={{
                pl: { xs: 0, lg: 6 },
                pt: { xs: 0, lg: 6 },
                py: { xs: 0, lg: 6 },
              }}
              size={{ xs: 12, lg: 6 }}
            >
              <Stack spacing={{ xs: 3, lg: 5 }}>
                <Typography
                  variant="h5SolidDivider"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PublicSvgIcon name="shopping_cart" /> Buyer overview
                </Typography>
                <UserDetails
                  transactionData={onlineTransactionData}
                  clientId={clientId}
                  userTransactionRole="buyer"
                />
                <BuyerLedger transactionData={onlineTransactionData} />
              </Stack>
            </Grid>

            <Grid
              id="seller"
              sx={{
                pr: { xs: 0, lg: 6 },
                pt: { xs: 0, lg: 6 },
                py: { xs: 0, lg: 6 },
                scrollMarginTop: theme.spacing(10),
              }}
              size={{ xs: 12, lg: 6 }}
            >
              <Stack spacing={{ xs: 3, lg: 5 }}>
                <Typography
                  variant="h5SolidDivider"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PublicSvgIcon name="storefront" /> Seller overview
                </Typography>
                <UserDetails
                  transactionData={onlineTransactionData}
                  clientId={clientId}
                  userTransactionRole="seller"
                />
                <SellerLedger transactionData={onlineTransactionData} />
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, lg: 474 }}>
          <Stack spacing={{ xs: 3, lg: 0 }}>
            <Box id="timeline" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <TransactionTimelineSection
                timeline={onlineTransactionData.timeline || []}
                statusData={statusData}
              />
            </Box>
            <Box id="extras" sx={{ scrollMarginTop: theme.spacing(10) }}>
              <ExtrasSection transactionData={onlineTransactionData} />
            </Box>
          </Stack>
        </Grid>
      </Box>
    </Box>
  );
}
